import useGetFormFields from '@hooks/useGetFormFields';
import {
  FormBuilder,
  FormBuilderContainer,
  GenericObj,
  getDefaultValue,
  NestedObject,
} from '@gemini/lux-ds-pms';
import { useEffect, useMemo, useRef, useState } from 'react';
import { flattenFields } from './utils';
import { usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import {
  CountryListDTO,
  InsuranceDTO,
  MasterLookUpDTO,
} from '@utils/interfaces';

import { Divider, Stack } from '@gemini/fe-ds-core';
import { UseFormReturn } from 'react-hook-form';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { mergeObjects } from '@utils/functions';
import { IChangeEvent, ISaveEvent, OBEvents } from '@utils/events/events';
import { useEvent } from '@gemini/fe-utils';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import { FormWrapper } from './FormBuilderStyles';

interface FormBuilderObProps {
  formIds: Array<FormIds>;
  setEnableButton: (arg: boolean) => void;
  onUnmount?: (formData?: NestedObject) => void;
  needUpdates?: boolean;
  optionsList?: Array<FormBuilderObOptions>;
  patientIndex?: number;
  isVisible?: boolean;
  resetFieldsContactsDetails?: boolean;
  mykey?: string;
  handleSave?: (payload: ISaveEvent, formData?: GenericObj) => void;
}

export interface FormBuilderObOptions {
  insurances?: InsuranceDTO;
  countryList?: CountryListDTO;
  countryStatesList?: CountryListDTO;
  genderList?: MasterLookUpDTO;
  holders?: MasterLookUpDTO;
}

/**
 * Recupera le informazioni da servizio e passa i dati al FormBuilder.
 */
const FormBuilderOb = ({
  formIds,
  setEnableButton,
  onUnmount = () => {},
  needUpdates = false,
  optionsList,
  patientIndex = 0,
  isVisible = true,
  resetFieldsContactsDetails,
  mykey,
  handleSave = () => {},
}: FormBuilderObProps) => {
  const { emit, subscribe } = useEvent();
  const getCurrentPatient = usePatientStore(state => state.getCurrentPatient);
  const userInfoFromWCS = useOnlineBookingStore(state => state.infoUser);
  const brandNameFromStore = useOnlineBookingStore(state => state.brandName);
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const setCheckboxValue = useOnlineBookingStore(
    state => state.setContactDetailsCheckboxStatus,
  );
  const patients = usePatientStore(state => state.patients);

  const contactsDetails = usePatientStore(state => state.contactsDetails);
  const methods = useRef<UseFormReturn<GenericObj> | null>(null);
  const [formIsReady, setFormIsReady] = useState(false);
  const datasIsSet = useRef(false);
  const [formDefValues, setFormDefValues] = useState<GenericObj | undefined>();
  const [formDefValuesList, setFormDefValuesList] = useState<
    Array<GenericObj> | undefined
  >();

  const datas = useGetFormFields({
    formIds,
    optionsList,
    patientIndex,
  });

  useEffect(() => {
    if (datas && !datasIsSet.current) {
      console.log('#datas: ', datas, formIds, patientIndex);
      datasIsSet.current = true;
      // setConfigForm(datas);
      const patient = getCurrentPatient(patientIndex);
      // console.log('#patient: ', patient);
      const objs = formIds.map((formId, index) => {
        console.log('#FormbuilderOB | formId', formId);
        const flatten: GenericObj = flattenFields(
          datas[index].data,
          formId === FormIds.ContactInformation
            ? { contactsDetails: contactsDetails }
            : (patient as any),
        );

        if (formId === FormIds.PatientDetail) {
          flatten.patientDetails.country = 'US';
          flatten.patientDetails.phoneNumber.type = 'mobile';

          if (userInfoFromWCS) {
            // by default set phone number prefix and country (only if not already set)
            if (!flatten.patientDetails.phoneNumber?.phoneNumber) {
              flatten.patientDetails.phoneNumber = {
                ...flatten.patientDetails.phoneNumber,
                prefix: userInfoFromWCS?.phoneNumber?.prefix,
                type: userInfoFromWCS?.phoneNumber?.type,
                country: userInfoFromWCS?.phoneNumber?.country,
              };
            }

            // If is first patient and there are no other patientDetails in store, prefill with userInfoFromWCS
            if (!patient?.patientDetails && patientIndex === 0) {
              flatten.patientDetails.firstName = userInfoFromWCS?.firstName;
              flatten.patientDetails.lastName = userInfoFromWCS?.lastName;
              flatten.patientDetails.email = userInfoFromWCS?.email;
              flatten.patientDetails.dateOfBirth = userInfoFromWCS?.dateOfBirth;
              flatten.patientDetails.phoneNumber = {
                ...flatten.patientDetails.phoneNumber,
                phoneNumber: userInfoFromWCS?.phoneNumber?.number,
              };
            }
          }
        }
        if (formId === FormIds.ContactInformation) {
          flatten.contactsDetails.phoneNumber.type = 'mobile';
        }
        if (
          formId === FormIds.PatientDetailPrimaryInsurance ||
          formId === FormIds.PatientDetailSecondaryInsurance
        ) {
          // Verifica e crea medicalInsuranceDetails se non esiste
          if (flatten.patientDetails.medicalInsuranceDetails) {
            flatten.patientDetails.medicalInsuranceDetails.country = 'US';
            if (flatten.patientDetails.medicalInsuranceDetails.phoneNumber) {
              flatten.patientDetails.medicalInsuranceDetails.phoneNumber.type =
                'mobile';
            } else {
              flatten.patientDetails.medicalInsuranceDetails.phoneNumber = {
                prefix: '',
                phoneNumber: '',
                type: 'mobile',
              };
            }
          } else {
            flatten.patientDetails = {
              ...flatten.patientDetails,
              medicalInsuranceDetails: {
                country: 'US',
                phoneNumber: {
                  prefix: '',
                  phoneNumber: '',
                  type: 'mobile',
                },
              },
            };
          }
          if (flatten.patientDetails.visionInsuranceDetails) {
            flatten.patientDetails.visionInsuranceDetails.country = 'US';
            if (flatten.patientDetails.visionInsuranceDetails.phoneNumber) {
              flatten.patientDetails.visionInsuranceDetails.phoneNumber.type =
                'mobile';
            } else {
              flatten.patientDetails.visionInsuranceDetails.phoneNumber = {
                prefix: '',
                phoneNumber: '',
                type: 'mobile',
              };
            }
          } else {
            flatten.patientDetails = {
              ...flatten.patientDetails,
              visionInsuranceDetails: {
                country: 'US',
                phoneNumber: {
                  prefix: '',
                  phoneNumber: '',
                  type: 'mobile',
                },
              },
            };
          }
        }

        console.log('#FormbuilderOB | flatten', flatten);
        return flatten;
      });
      // setDefaultValues(objs);
      const res = objs.reduce((prev, curr) => {
        return mergeObjects(prev, curr, true);
      }, {}) as GenericObj;
      //BEGIN TEMPORARY: OPTIN SMS DEFAULT TRUE IN RONA -> in futuro recuperiamo da BFF e questo verra rimosso e gestito da BFF
      if (brandNameFromStore && formConfigs?.customFieldsMarketing?.children) {
        const smsOptinIndex =
          formConfigs.customFieldsMarketing.children.findIndex(
            c => c.id === 'SMS',
          );
        const customFieldsMarketingFormIndex = formIds.indexOf(
          FormIds.CustomFieldsMarketing,
        );

        if (smsOptinIndex >= 0 && customFieldsMarketingFormIndex >= 0) {
          const smsField = `field${smsOptinIndex + 1}`;
          res.contactsDetails.storeCustomFieldsMarketing[smsField] = true;
          objs[
            customFieldsMarketingFormIndex
          ].contactsDetails.storeCustomFieldsMarketing[smsField] = true;
        }
      }
      //END TEMPORARY: OPTIN SMS DEFAULT TRUE IN RONA -> in futuro recuperiamo da BFF e questo verra rimosso e gestito da BFF

      // if (res && formIds.includes(FormIds.PatientDetail)) {
      //   res.patientDetails.country = 'US';
      // }
      // if (
      //   res &&
      //   (formIds.includes(FormIds.PatientDetailPrimaryInsurance) ||
      //     formIds.includes(FormIds.PatientDetailSecondaryInsurance))
      // ) {
      //   res.patientDetails.visionInsuranceDetails.country = 'US';
      //   res.patientDetails.medicalInsuranceDetails.country = 'US';
      // }
      setFormDefValues(res);
      setFormDefValuesList(objs);
      console.log('#objs: ', objs, patientIndex);
      console.log('#res: ', res, patientIndex);
    }
  }, [datas]);

  useEffect(() => {
    console.log('#ImportFlow | start');
    if (
      formIsReady &&
      methods.current &&
      typeof resetFieldsContactsDetails !== 'undefined' &&
      resetFieldsContactsDetails &&
      formIds.includes(FormIds.ContactInformation)
    ) {
      console.log('#ImportFlow|FormBuilderOb.patientDetails...');
      const patientToImport = patients.find(
        p =>
          p?.order ===
          Math.min(
            ...patients.map(obj =>
              typeof obj?.order !== 'undefined' ? obj.order : 99,
            ),
          ),
      );

      // Estrai i dettagli del paziente
      const { firstName, lastName, phoneNumber, email, dateOfBirth } =
        patientToImport?.patientDetails || {};

      // Imposta i valori individualmente
      methods.current.setValue('contactsDetails.firstName', firstName, {
        shouldValidate: true,
      });
      methods.current.setValue('contactsDetails.lastName', lastName, {
        shouldValidate: true,
      });
      methods.current.setValue('contactsDetails.phoneNumber', phoneNumber, {
        shouldValidate: true,
      });
      methods.current.setValue('contactsDetails.email', email, {
        shouldValidate: true,
      });
      methods.current.setValue('contactsDetails.dateOfBirth', dateOfBirth, {
        shouldValidate: true,
      });

      const subscription = methods.current.watch((value, { name }) => {
        // controlla che non siano i customFields e che il valore si diverso da quello inizialmente impostato
        if (
          name &&
          !name.includes('storeCustomFieldsMarketing') &&
          getDefaultValue(name, value) !==
            getDefaultValue(name, patientToImport?.patientDetails)
        ) {
          setCheckboxValue(false);
          subscription.unsubscribe();
        }
      });
      return () => subscription.unsubscribe();
    }
    if (methods.current) {
      methods.current.watch((value, { name }) => {
        if (formIds.includes(FormIds.Insurance)) {
          const patient = getCurrentPatient(patientIndex);

          if (
            name &&
            patient?.scheduleExam &&
            getDefaultValue(name, value) !==
              getDefaultValue(name, patient?.insurance)
          ) {
            console.log('## Insurance Change - Event EMIT ##');
            emit<IChangeEvent>(OBEvents.apptChange, {
              type: 'insurance',
            });
          }
        }
      });
    }
    return;
  }, [resetFieldsContactsDetails, formIsReady]);

  const key = useMemo(() => `OBContainer_${mykey}`, []);

  useEffect(() => {
    const unsubscribe = subscribe<ISaveEvent>(
      OBEvents.insSave,
      payload => {
        console.log('#FormBuilderOb | handleSave', payload);
        if (
          payload.currentStep === StepsStep.INSURANCE &&
          typeof handleSave === 'function'
        ) {
          handleSave(
            payload,
            methods.current ? methods.current.getValues() : undefined,
          );
        }
      },
      {
        self: true,
      },
    );

    return () => unsubscribe();
  }, [handleSave, subscribe]);

  // console.log(`#${key}|FormBuilder.ob: `, {
  //   formDefValues,
  //   patientIndex,
  //   formDefValuesList,
  // });

  return (
    <FormWrapper isVisible={isVisible} brandName={!!brandNameFromStore}>
      {formDefValues && formDefValuesList && methods && (
        <FormBuilderContainer
          optionForm={{
            defaultValues: formDefValues,
            mode: brandNameFromStore ? 'onBlur' : 'onChange',
          }}
          onUnmount={onUnmount}
          setEnableButton={setEnableButton}
          myRef={methods}
          setFormIsReady={setFormIsReady}
          key={key}
          mykey={key}
        >
          {formIds.map((formId, index) => {
            return (
              <>
                <FormBuilder
                  mykey={`${key}|FormBuilder_${formId}`}
                  key={`${key}|FormBuilder_${formId}`}
                  form={datas[index].data}
                  defaultValues={formDefValuesList[index]}
                  needUpdates={needUpdates}
                />
                {index < formIds.length - 1 &&
                  datas[index].data.some(
                    d =>
                      (!d.children && d.visibility) ||
                      d.children?.some(c => c.visibility),
                  ) && (
                    <Stack sx={{ padding: '0 1rem' }}>
                      <Divider />
                    </Stack>
                  )}
              </>
            );
          })}
        </FormBuilderContainer>
      )}
    </FormWrapper>
  );
};

export default FormBuilderOb;
